import { useT } from '@/lib/i18n';
import { type OcrProgress, ocrImage } from '@/lib/ocr/tesseract';
import { useSourceStore } from '@/lib/state/sourceStore';
import { useCallback, useEffect, useState } from 'react';

interface PhotoCaptureProps {
  onDone?: () => void;
}

interface Staged {
  file: File;
  url: string;
}

export function PhotoCapture({ onDone }: PhotoCaptureProps) {
  const { t } = useT();
  const addSource = useSourceStore((s) => s.addSource);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [progress, setProgress] = useState<OcrProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (staged) URL.revokeObjectURL(staged.url);
    };
  }, [staged]);

  const onFile = useCallback(
    (file: File) => {
      setError(null);
      if (staged) URL.revokeObjectURL(staged.url);
      const url = URL.createObjectURL(file);
      setStaged({ file, url });
      setProgress(null);
    },
    [staged],
  );

  const runOcr = async () => {
    if (!staged) return;
    setRunning(true);
    setError(null);
    setProgress({ status: 'starting', progress: 0 });
    try {
      const res = await ocrImage(staged.file, (p) => setProgress(p));
      const text = res.text.trim();
      if (!text) {
        setError(t('input.photo.err_no_text'));
        setRunning(false);
        return;
      }
      addSource({
        kind: 'image',
        label: staged.file.name || t('input.photo.default_label'),
        text,
        mime_type: staged.file.type,
        byte_size: staged.file.size,
      });
      URL.revokeObjectURL(staged.url);
      setStaged(null);
      setProgress(null);
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('input.photo.err_ocr'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="photo-capture">
      {!staged ? (
        <label
          className="flex flex-col items-center justify-center gap-2 px-4 py-8 border-[2.5px] border-dashed border-ink rounded-card bg-paper hover:bg-white/60 cursor-pointer text-center font-hand text-ink"
          data-testid="photo-dropzone"
        >
          <span className="text-4xl" aria-hidden>
            📷
          </span>
          <span className="text-[15px]">{t('input.photo.cta')}</span>
          <span className="font-caveat text-ink-soft text-[14px]">{t('input.photo.hint')}</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = '';
            }}
            disabled={running}
            data-testid="photo-file-input"
          />
        </label>
      ) : (
        <div className="flex gap-3 items-start">
          <img
            src={staged.url}
            alt={`Aperçu de ${staged.file.name}`}
            className="w-[140px] h-auto max-h-[200px] object-cover border-2 border-ink rounded-control shadow-ink-sm shrink-0"
          />
          <div className="flex-1 flex flex-col gap-2">
            <div className="font-hand text-[15px] text-ink truncate" title={staged.file.name}>
              {staged.file.name || t('input.photo.capture_fallback')}
            </div>
            <div className="font-caveat text-ink-soft text-[15px]">
              {(staged.file.size / 1024).toFixed(0)} kB
            </div>
            {progress ? (
              <div className="flex flex-col gap-1">
                <div className="font-hand text-[13px] text-ink-soft">
                  {progress.status} — {Math.round(progress.progress * 100)} %
                </div>
                <div className="h-2 border-2 border-ink rounded-full bg-paper overflow-hidden">
                  <div
                    className="h-full bg-accent-green"
                    style={{ width: `${Math.round(progress.progress * 100)}%` }}
                  />
                </div>
              </div>
            ) : null}
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={runOcr}
                disabled={running}
                className="px-3 py-2 min-h-[40px] bg-accent-green text-white font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-40"
                data-testid="photo-run-ocr"
              >
                {running ? t('input.photo.ocr_running') : t('input.photo.ocr_cta')}
              </button>
              <button
                type="button"
                onClick={() => {
                  URL.revokeObjectURL(staged.url);
                  setStaged(null);
                  setProgress(null);
                }}
                disabled={running}
                className="px-3 py-2 min-h-[40px] bg-white text-ink font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-40"
              >
                {t('input.photo.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {error ? (
        <p className="font-hand text-warn-text text-[14px]" role="alert">
          ⚠️ {error}
        </p>
      ) : null}
    </div>
  );
}
